import '../styles/globals.css';
import 'core-js/actual/string/replace-all';

import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import type React from 'react';
import { useEffect, useState } from 'react';
import { CookiesProvider } from 'react-cookie';
import MobileDetect from 'mobile-detect';
import { useSiftScience } from '@zg-rentals/sift-science';
import { ApolloProvider } from '@apollo/client';
import apolloClient from '../lib/apollo';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { ToastProvider as Constellation10ToastProvider } from '@zillow/constellation-10';
import { ToastProvider as Constellation8ToastProvider, ThemeConstellation } from '@zillow/constellation';
import { AppContextProvider } from '../contexts/AppContextProvider';
import pathsManifest from '../utils/pathsManifest';
import { HeadBase } from '@zg-rentals/next-components';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { ErrorPage } from '@zg-rentals/error-page-components';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import { Auth, useAuth } from '@zg-rentals/auth-comet';
import useFeatureFlagsHelper from '../hooks/useFeatureFlagsHelper';
import { setClient as setZCalendarClient } from '@zg-rentals/ts-z-calendar-client';
import { setClient as setSatelliteClient } from '@zg-rentals/ts-satellite-client';
import { setClient as setRentalManagerClient } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { rentalClient, rentalManagerClient } from '../httpClient';
import { useRegisterNotification } from '@zg-rentals/register-notification';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { trackComscoreEvent, trackUserEvent } from '../analytics';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';
import { ERROR_DETAILS, ERROR_MESSAGE } from '@zg-rentals/satellite-components';

const UserAnalytics = ({
  children,
}: {
  children: (arg: { encodedZuid: string; isImpersonator: boolean; isPostingPathAdmin: boolean }) => React.ReactNode;
}) => {
  const { user } = useAuth();
  const router = useRouter();
  const { email, encodedZuid, impersonatorEmail, impersonatorUserToken, isPostingPathAdmin = false } = user ?? {};

  useSiftScience({ email, isImpersonator: Boolean(impersonatorUserToken) });
  useZrmPageViewEvents(trackUserEvent, router.pathname, user);
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useRegisterNotification({ ref: 'SATELLITE_LANDLORD_WEB' });

  const isImpersonator = (!!impersonatorEmail && !!impersonatorUserToken && isPostingPathAdmin) || false;

  return children({ encodedZuid: encodedZuid || '', isImpersonator, isPostingPathAdmin });
};

setZCalendarClient(rentalClient);
setSatelliteClient(rentalClient);
setRentalManagerClient(rentalManagerClient);

export default function MyApp({ Component, pageProps }: AppProps) {
  useBootstrap(() => import('../bootstrap'));

  const paths = pathsManifest();

  const { enableUserObjectOptimization } = useFeatureFlagsHelper();
  const initialUser = enableUserObjectOptimization && typeof window !== 'undefined' ? window.__USER_INFO__ : undefined;

  const [isClient, setIsClient] = useState(false);
  const [hasMessageListError, setHasMessageListError] = useState<boolean | null>(null);

  const isMobileDevice =
    isClient && typeof window !== 'undefined' ? !!new MobileDetect(window.navigator.userAgent).phone() : false;

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <>
      <HeadBase applicationName="satellite-landlord-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER} />

      <SCThemeProvider theme={ThemeConstellation}>
        <RpErrorBoundary
          fallback={
            <ErrorPage
              errorMessage={ERROR_MESSAGE}
              errorDetails={ERROR_DETAILS}
              action={<a href={paths.homePage}>Return to Zillow Rental Manager</a>}
            />
          }
        >
          <DefaultQueryProvider
            defaultOptions={{
              queries: {
                staleTime: 20 * 1000,
              },
            }}
          >
            <Auth.Provider
              client={rentalManagerClient}
              // hydrate initial data with user from Java API
              queryOptions={{ initialData: initialUser }}
            >
              <ApolloProvider client={apolloClient}>
                <UserAnalytics>
                  {({ isImpersonator, isPostingPathAdmin }) => (
                    <>
                      <AppContextProvider
                        value={{
                          isImpersonator,
                          isMobileDevice,
                          isPostingPathAdmin,
                          paths,
                          hasMessageListError,
                          setHasMessageListError,
                        }}
                      >
                        {/* @zg-rentals/touring utilizes Constellation-8 so both versions will need to be loaded until migrated */}
                        <Constellation8ToastProvider placement="bottom-end">
                          <Constellation10ToastProvider placement="bottom-end">
                            <CookiesProvider>
                              <Component {...pageProps} />
                            </CookiesProvider>
                          </Constellation10ToastProvider>
                        </Constellation8ToastProvider>
                      </AppContextProvider>
                    </>
                  )}
                </UserAnalytics>
              </ApolloProvider>
            </Auth.Provider>
          </DefaultQueryProvider>
        </RpErrorBoundary>
      </SCThemeProvider>
    </>
  );
}
